import {getTestProps} from '@benchling/js-test-utils';
import {ActionIcon, Avatar, createStyles, Group, Tooltip} from '@mantine/core';
import React, {useCallback, useMemo} from 'react';

import navigateToURL from 'lib/navigateToURL';
import {ChatSettingsTab} from 'llm/chat-library/sidepanel/AISidepanelV2GlobalClientState';
import {agentHomepageUrl, chatSettingsModalUrl} from 'llm/routes/UrlUtils';
import type {Routine} from 'llm/routines/RoutineTypes';
import useRoutines from 'llm/routines/useRoutines';
import {getAvatarInitials} from 'ui-library/avatar/Avatar';
import BenchlingIcon from 'ui-library/icons/BenchlingIcon';

import SidebarSectionHeader from './SidebarSectionHeader';

const AVATAR_SIZE_PX = 32;

/** Shown when an agent has no usable name, so its avatar still has a stable label/tooltip/initials. */
const PLACEHOLDER_AGENT_NAME = 'Untitled Agent';

const useStyles = createStyles((theme) => ({
  root: {
    marginBottom: theme.spacing.xs,
  },
  // A plain flex row rather than a Mantine Group: Group forces `flex-grow: 0` on every child, which
  // would collapse the flex:1 clip box below to zero width and hide the avatars.
  avatarRow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing.xs,
    marginLeft: 4,
    minWidth: 0,
  },
  // Clips the avatar strip to the available width instead of scrolling it: extra agents are revealed
  // by dragging the drawer wider (which grows this box) or via the trailing chevron. No scrollbar.
  avatarClip: {
    flex: 1,
    minWidth: 0,
    overflow: 'hidden',
  },
  avatarGroup: {
    width: 'max-content',
  },
  // Shared sizing and button reset for both the create ("+") and per-agent avatars. Avatar renders
  // as a <button>, so we strip the native chrome and pin a fixed circular size.
  avatar: {
    width: AVATAR_SIZE_PX,
    height: AVATAR_SIZE_PX,
    minWidth: AVATAR_SIZE_PX,
    cursor: 'pointer',
    border: 'none',
    padding: 0,
  },
  // Deep-navy create button matching the design; overrides the filled variant's mid-tone background.
  createAvatar: {
    backgroundColor: theme.colors.indigo[9],
  },
  viewAll: {
    width: AVATAR_SIZE_PX,
    height: AVATAR_SIZE_PX,
    minWidth: AVATAR_SIZE_PX,
    color: theme.colors.dark[7],
  },
}));

/**
 * The agent's display name, normalizing null/blank/whitespace-only names to a placeholder so every
 * avatar has a non-empty accessible name and tooltip.
 */
export function getAgentDisplayName(name: string | null): string {
  const trimmed = name?.trim() ?? '';
  return trimmed === '' ? PLACEHOLDER_AGENT_NAME : trimmed;
}

function getTimestamp(value: string): number {
  const timestamp = new Date(value).getTime();
  return Number.isNaN(timestamp) ? 0 : timestamp;
}

function AgentAvatar({routine}: {routine: Routine}): JSX.Element {
  const {classes} = useStyles();
  const displayName = getAgentDisplayName(routine.name);
  const handleClick = useCallback(() => {
    navigateToURL(agentHomepageUrl(routine.id));
  }, [routine.id]);

  return (
    <Tooltip label={displayName} withinPortal={true} openDelay={300} position="top">
      <Avatar
        component="button"
        type="button"
        radius="xl"
        color="violet"
        aria-label={displayName}
        onClick={handleClick}
        className={classes.avatar}
        data-amp-safe={false}
        {...getTestProps({component: 'SidebarAgentsList', element: 'agentAvatar', key: routine.id})}
      >
        {getAvatarInitials({name: displayName})}
      </Avatar>
    </Tooltip>
  );
}

/**
 * Recently created/updated strip of agent (routine) avatars for the redesigned chat sidebar. The
 * avatar row clips to the available width (no scrollbar): widening the drawer reveals more avatars,
 * and the trailing chevron opens the full ordered Agent list. Each avatar deep-links to that agent's
 * homepage; the leading "+" opens Agent creation. Renders nothing while loading or when there are no
 * configured agents.
 *
 * Callers gate mounting on `isAgentsFeatureEnabled() && llmEnableRoutineHomepage`; this component
 * does not re-check those flags.
 */
export default function SidebarAgentsList({
  onViewAllAgents,
}: {
  onViewAllAgents: (orderedRoutines: Array<Routine>) => void;
}): JSX.Element | null {
  const {classes, cx} = useStyles();
  // Reuses the shared, cached routines hook. Its payload includes fields this strip doesn't render
  // (message blocks, trigger config, timestamps); since RoutinesQuery is usually already warm from
  // other agent surfaces, that's acceptable for now. TODO(BNCH-171285): if this always-mounted strip
  // proves costly, back it with a slim id/name-only projection instead.
  const {routines, isLoading} = useRoutines();

  const handleCreateAgent = useCallback(() => {
    navigateToURL(chatSettingsModalUrl(ChatSettingsTab.AGENTS, {createNew: true}));
  }, []);

  // TODO(BNCH-172613): extend sorting to factor in recent runs, not just create/edit time.
  const orderedRoutines = useMemo(
    () =>
      [...routines].sort((left, right) => {
        const leftActivity = Math.max(getTimestamp(left.createdAt), getTimestamp(left.modifiedAt));
        const rightActivity = Math.max(getTimestamp(right.createdAt), getTimestamp(right.modifiedAt));
        return rightActivity - leftActivity || left.id.localeCompare(right.id);
      }),
    [routines]
  );

  if (isLoading || routines.length === 0) {
    return null;
  }

  return (
    <div className={classes.root} {...getTestProps({component: 'SidebarAgentsList', element: 'root'})}>
      <SidebarSectionHeader>Agents</SidebarSectionHeader>
      <div className={classes.avatarRow}>
        <div className={classes.avatarClip}>
          <Group
            spacing="xs"
            noWrap={true}
            className={classes.avatarGroup}
            {...getTestProps({component: 'SidebarAgentsList', element: 'avatarGroup'})}
          >
            <Tooltip label="Create an agent" withinPortal={true} openDelay={300} position="top">
              <Avatar
                component="button"
                type="button"
                radius="xl"
                color="indigo"
                variant="filled"
                aria-label="Create an agent"
                onClick={handleCreateAgent}
                className={cx(classes.avatar, classes.createAvatar)}
                {...getTestProps({component: 'SidebarAgentsList', element: 'addAgent'})}
              >
                <BenchlingIcon icon="plus" />
              </Avatar>
            </Tooltip>
            {orderedRoutines.map((routine) => (
              <AgentAvatar key={routine.id} routine={routine} />
            ))}
          </Group>
        </div>
        <Tooltip label="View all agents" withinPortal={true} openDelay={300} position="top">
          <ActionIcon
            aria-label="View all agents"
            className={classes.viewAll}
            onClick={() => {
              onViewAllAgents(orderedRoutines);
            }}
            variant="subtle"
            {...getTestProps({component: 'SidebarAgentsList', element: 'viewAllAgents'})}
          >
            <BenchlingIcon icon="chevron-right" />
          </ActionIcon>
        </Tooltip>
      </div>
    </div>
  );
}
